import React, { useState, useEffect, useContext } from "react"
import moment from "moment-timezone"
import { Send, MessageSquare } from "lucide-react"

import { BookingContext } from "../../../utils/providers/isolated-booking"
import { db, funEU } from "../../../utils/firebase"

import styles from "./messages.module.scss"

const notifyAgentCallable = funEU.httpsCallable("notifyAgentOfClientMessage")

/**
 * Messages — let the client leave a message for their sales agent. One-way for
 * now: messages are recorded on the booking and emailed to the agent (who
 * replies by email); the thread shows the client's own sent messages.
 *
 * Message timestamps are formatted in Europe/London (portal root default).
 */
export default function Messages() {
	const { bookingID, agent } = useContext(BookingContext)

	const [thread, setThread] = useState([])
	const [message, setMessage] = useState("")
	const [sending, setSending] = useState(false)
	const [error, setError] = useState("")

	useEffect(() => {
		if (!bookingID) return

		const unsubscribe = db
			.collection(`bookings/${bookingID}/messages`)
			.orderBy("created", "asc")
			.onSnapshot(
				(snapshot) => {
					setThread(
						snapshot.docs.map((doc) => {
							const { body, created } = doc.data()
							return {
								id: doc.id,
								body,
								when: created?.seconds ? moment(created.seconds, "X").format("DD MMM YYYY, HH:mm") : "",
							}
						}),
					)
				},
				(err) => console.log("Messages listener error:", err),
			)

		return () => unsubscribe()
	}, [bookingID])

	const send = async () => {
		const body = message.trim()
		if (!body) return

		setSending(true)
		setError("")
		try {
			await notifyAgentCallable({ bookingID, message: body })
			setMessage("")
		} catch (err) {
			setError(err?.message || "We couldn't send your message. Please try again.")
		} finally {
			setSending(false)
		}
	}

	const agentFirst = agent?.first_name

	return (
		<div className={styles.messages}>
			<header className={styles.head}>
				<h1>Messages</h1>
				<p>{agentFirst ? `Have a question? Leave a message and ${agentFirst} will get back to you by email.` : "Have a question? Leave a message and your agent will get back to you by email."}</p>
			</header>

			<section className={styles.threadCard}>
				{thread.length === 0 ? (
					<div className={styles.empty}>
						<MessageSquare size={26} />
						<p>No messages yet. Send your agent a message below and they'll reply by email.</p>
					</div>
				) : (
					<div className={styles.thread}>
						{thread.map((item) => (
							<div
								key={item.id}
								className={styles.bubbleRow}>
								<div className={styles.bubble}>
									<p>{item.body}</p>
									{item.when ? <span className={styles.bubbleTime}>You &middot; {item.when}</span> : <span className={styles.bubbleTime}>Sending…</span>}
								</div>
							</div>
						))}
					</div>
				)}
			</section>

			<section className={styles.composer}>
				<textarea
					placeholder="Type your message…"
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					rows={4}
				/>
				{error ? <p className={styles.error}>{error}</p> : null}
				<button
					type="button"
					className={styles.send}
					onClick={send}
					disabled={sending || !message.trim()}>
					<Send size={16} />
					{sending ? "Sending…" : "Send message"}
				</button>
				<p className={styles.note}>Your agent will reply by email, so keep an eye on your inbox.</p>
			</section>
		</div>
	)
}
